import { useEffect, useRef, useState } from "react";

import type { Template } from "../types";
import { renderTemplate } from "../lib/renderTemplate";

interface Props {
  template: Template;
  canvases: Map<string, HTMLCanvasElement>;
  /** Overrides keyed by slot id */
  slotOverrides?: Map<string, HTMLImageElement>;
  highlightedLayerZ?: number | null;
  /** Max rendered height in pixels (caps the scale alongside container width) */
  maxHeight?: number;
}

export function PreviewCanvas({ template, canvases, slotOverrides, highlightedLayerZ, maxHeight }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.5);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      if (w === 0) return;
      const sw = w / template.width;
      const sh = maxHeight ? maxHeight / template.height : Infinity;
      setScale(Math.min(1, sw, sh));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [template.width, template.height, maxHeight]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    renderTemplate(canvas, {
      template,
      canvases,
      slotOverrides,
      drawSlotPlaceholders: true,
      highlightedLayerZ,
    });
  }, [template, canvases, slotOverrides, highlightedLayerZ]);

  const displayW = template.width * scale;
  const displayH = template.height * scale;

  return (
    <div ref={wrapperRef} className="w-full flex items-start justify-center">
      <div
        className="rounded-xl overflow-hidden border shadow bg-[conic-gradient(at_25%_25%,#3a3a3a_25%,#2a2a2a_0_50%,#3a3a3a_0_75%,#2a2a2a_0)] bg-[length:24px_24px]"
        style={{ width: displayW, height: displayH }}
      >
        <canvas
          ref={canvasRef}
          style={{ width: displayW, height: displayH, display: "block" }}
        />
      </div>
    </div>
  );
}
